import { isUUIDLike } from '@wilson/utils';

/**
 * Prefix used to identify temporary IDs for entities that haven't been persisted yet.
 */
const TEMP_ID_PREFIX = 'new-';

/**
 * Base class for entities that require client-side ID management.
 *
 * @example
 * ```typescript
 * class MyModel extends Identifiable {
 *   constructor(data: MyModel) {
 *     super(data);
 *   }
 * }
 *
 * const newModel = new MyModel({ name: '...' }); // auto-generates temp ID
 * const existingModel = new MyModel({ id: 'uuid', name: '...' });
 * ```
 */
export class Identifiable {
  // TODO(@mattarau): Type this as `UUID | \`new-${UUID}\`` — requires a wider refactoring across the codebase.
  readonly id: string;

  constructor({ id }: Partial<Identifiable> = {}) {
    this.#validateId(id);
    this.id = id ?? Identifiable.generateTempId();
  }

  /**
   * Validates id field if provided.
   * Private method ensures validation cannot be skipped.
   */
  #validateId(id: string | undefined): void {
    if (id == null) return;

    const uuidPart = Identifiable.stripNewPrefix(id);
    if (!isUUIDLike(uuidPart)) {
      throw new Error(
        `Identifiable: id must be a valid UUID (with optional "${TEMP_ID_PREFIX}" prefix)`,
      );
    }
  }

  /**
   * Generates a temporary ID for a new entity.
   * Format: `new-{uuid}`
   */
  static generateTempId(): string {
    return `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
  }

  /**
   * Checks if the given ID is a temporary ID (starts with `new-` prefix).
   */
  static isNew(id: string): boolean {
    return id.startsWith(TEMP_ID_PREFIX);
  }

  /**
   * Strips the `new-` prefix from a temporary ID.
   * Returns the ID unchanged if it's not a temporary ID.
   *
   * Use this when preparing IDs for API calls that don't accept the prefix.
   */
  static stripNewPrefix(id: string): string {
    return Identifiable.isNew(id) ? id.substring(TEMP_ID_PREFIX.length) : id;
  }
}
